import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

interface ScanningOverlayProps {
  /** Label shown under the spinner. Defaults to the timesheet AI-scan message. */
  label?: string;
  sx?: SxProps<Theme>;
}

/**
 * Absolutely-positioned busy overlay that dims its (position: relative) parent
 * while an async scan runs. The background is driven by the mode-aware
 * `surfaceScrim` token so it stays a light scrim in light mode and dims to a
 * dark translucent layer in dark mode instead of flashing white (TRE-16072).
 */
export default function ScanningOverlay({
  label = t('approvals.driver_day.scanning_timesheet'),
  sx,
}: ScanningOverlayProps) {
  return (
    <Box
      aria-label="scanning timesheet"
      sx={[
        (theme) => ({
          alignItems: 'center',
          bgcolor: theme.semantic.surfaceScrim,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          height: '100%',
          justifyContent: 'center',
          left: 0,
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 1,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <CircularProgress size={18} />
      <Typography
        variant="body2"
        sx={[
          { fontWeight: 500 },
          // The treadBlue brand hue reads at only ~2.9:1 on the dark scrim (fails
          // WCAG AA), so switch to the mode-aware ink there; light keeps the blue.
          (theme) => ({
            color:
              theme.palette.mode === 'dark'
                ? theme.semantic.textPrimary
                : theme.brandV2.colors.treadBlue,
          }),
        ]}
      >
        {label}
      </Typography>
    </Box>
  );
}
